import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConfirmDialog } from '../../shared/components/ConfirmDialog';
import { LoadingButton } from '../../shared/components/LoadingButton';
import { GlowAlert } from '../../shared/components/GlowAlert';
import { useUploadImage } from '../useUploadImage';
import {
    deleteUpload,
    errorMessage,
    fetchUploadFile,
    formatBytes,
    getUpload,
    isImage,
    kindLabel,
    refreshMetadata,
    renameUpload,
    retryUpload,
    STATUS_LABELS,
    UploadDetail,
    UploadStatus,
} from '../uploadsApi';

import { ExifPanel } from './ExifPanel';
import { FacesPanel } from './FacesPanel';

// What to say in the Extracted accordion when there is nothing yet: why, by status.
const EXTRACTION_STATUS_NOTE: Partial<Record<UploadStatus, string>> = {
    stored: 'Not read: nothing can extract this type yet.',
    queued: 'Waiting for the ingestion worker.',
    processing: 'Being read now.',
    failed: 'Extraction failed (see the reason in Status above).',
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.25 }}>
        <Typography variant="body2" color="text.secondary" sx={{ width: 130, flexShrink: 0 }}>
            {label}
        </Typography>
        <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word', minWidth: 0 }}>
            {children}
        </Typography>
    </Box>
);

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.8 }}>
        {children}
    </Typography>
);

const NAME_SOURCES: Record<string, string> = {
    filename: 'cleaned-up filename',
    llm: 'written by the model',
    user: 'edited by a person',
};

export interface UploadDetailDialogProps {
    uploadId: string;
    onClose: () => void;
    /** Called after something changed, so the list can catch up. `deleted` when the upload is gone. */
    onChanged: (change: { deleted: boolean }) => void;
}

/** One upload with everything known about it, and what the admin can do with it. */
export const UploadDetailDialog: React.FC<UploadDetailDialogProps> = ({ uploadId, onClose, onChanged }) => {
    const [detail, setDetail] = useState<UploadDetail | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [name, setName] = useState('');
    // Which action is running (rename, retry, ...): all of them wait for it, only its button spins.
    const [action, setAction] = useState<string | null>(null);
    const busy = action !== null;
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const image = detail ? isImage(detail.mime_type) : false;
    const pictureUrl = useUploadImage(uploadId, image && detail?.storage.file_present === true);

    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const loaded = await getUpload(uploadId);
            setDetail(loaded);
            setName(loaded.name);
        } catch (err) {
            setError(errorMessage(err, 'Failed to load the upload'));
        }
    }, [uploadId]);

    const refresh = async () => {
        setRefreshing(true);
        setNotice(null);
        try {
            await load();
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        setDetail(null);
        setNotice(null);
        load();
    }, [load]);

    const run = async (name: string, perform: () => Promise<string | void>, changed: { deleted: boolean } | null) => {
        setAction(name);
        setError(null);
        setNotice(null);
        try {
            const message = await perform();
            if (message) setNotice(message);
            if (changed) onChanged(changed);
            return true;
        } catch (err) {
            setError(errorMessage(err, 'That did not work'));
            return false;
        } finally {
            setAction(null);
        }
    };

    const rename = async () => {
        if (await run('rename', async () => {
            await renameUpload(uploadId, name.trim());
            return 'Renamed.';
        }, { deleted: false })) {
            await load();
        }
    };

    const readAgain = async () => {
        if (await run('read-again', async () => {
            await refreshMetadata(uploadId);
            return 'Metadata read again from the stored file.';
        }, { deleted: false })) {
            await load();
        }
    };

    const retry = async () => {
        if (await run('retry', async () => {
            await retryUpload(uploadId);
            return 'Queued to be read again.';
        }, { deleted: false })) {
            await load();
        }
    };

    const remove = async () => {
        const removed = await run('delete', async () => {
            const result = await deleteUpload(uploadId);
            return result.file_removed === null ? 'Deleted, but the file could not be removed from disk.' : undefined;
        }, { deleted: true });
        setConfirmingDelete(false);
        if (removed) onClose();
    };

    const download = async () => {
        if (!detail) return;
        await run('download', async () => {
            const blob = await fetchUploadFile(uploadId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = detail.original_filename;
            link.click();
            URL.revokeObjectURL(url);
        }, null);
    };

    const nameChanged = detail !== null && name.trim() !== detail.name && name.trim() !== '';

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="upload-detail-title">
            <DialogTitle id="upload-detail-title" sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {detail?.name ?? 'Upload'}
                </Box>
                <IconButton aria-label="Refresh" onClick={refresh} disabled={refreshing || busy} size="small">
                    {refreshing ? <CircularProgress size={18} aria-label="Refreshing" /> : <RefreshIcon fontSize="small" />}
                </IconButton>
                <IconButton aria-label="Close" onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}
                {notice && <GlowAlert severity="success" sx={{ mb: 2 }}>{notice}</GlowAlert>}
                {!detail && !error && <GlowAlert severity="info">Loading…</GlowAlert>}

                {detail && (
                    <>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            {image && (
                                <Box sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}>
                                    {pictureUrl ? (
                                        <Box
                                            component="img"
                                            src={pictureUrl}
                                            alt={detail.name}
                                            sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 1, backgroundColor: 'action.hover' }}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {detail.storage.file_present ? 'Loading the picture…' : 'The file is missing, so there is no picture.'}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Row label="Owner">{detail.user_name}</Row>
                                <Row label="Type">{kindLabel(detail.mime_type)} ({detail.mime_type})</Row>
                                <Row label="Size">{formatBytes(detail.size_bytes)}</Row>
                                <Row label="Status">
                                    <Chip size="small" label={STATUS_LABELS[detail.status]} color={detail.status === 'failed' ? 'error' : 'default'} />
                                    {detail.status_detail && ` ${detail.status_detail}`}
                                </Row>
                                <Row label="Uploaded">{new Date(detail.created_at).toLocaleString()}</Row>
                                <Row label="Updated">{new Date(detail.updated_at).toLocaleString()}</Row>
                                <Row label="Original name">{detail.original_filename}</Row>
                                <Row label="Display name">
                                    {detail.name} <Typography component="span" variant="caption" color="text.secondary">({NAME_SOURCES[detail.name_source]})</Typography>
                                </Row>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'flex-start' }}>
                                    <TextField
                                        size="small"
                                        label="Rename"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        inputProps={{ maxLength: 120 }}
                                        fullWidth
                                        helperText="Final: the model never replaces a name set here."
                                    />
                                    <LoadingButton variant="outlined" onClick={rename} loading={action === 'rename'} disabled={busy || !nameChanged}>
                                        Save
                                    </LoadingButton>
                                </Box>
                            </Box>
                        </Box>

                        <Accordion disableGutters variant="outlined" defaultExpanded sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Storage</AccordionSummary>
                            <AccordionDetails>
                                <Row label="Stored as">{detail.storage.given_filename}</Row>
                                <Row label="Directory">{detail.storage.directory}</Row>
                                <Row label="On disk">
                                    {detail.storage.file_present ? (
                                        <>
                                            <Chip size="small" color="success" variant="outlined" label="Present" />{' '}
                                            {detail.storage.size_on_disk !== null && formatBytes(detail.storage.size_on_disk)}
                                            {detail.storage.size_matches === false && (
                                                <Chip size="small" color="warning" label="Size differs from the record" sx={{ ml: 1 }} />
                                            )}
                                        </>
                                    ) : (
                                        <Chip size="small" color="error" label="Missing: the row points at nothing" />
                                    )}
                                </Row>
                                <Row label="SHA-256">
                                    <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{detail.sha256}</Box>
                                </Row>
                                {detail.same_file_elsewhere.length > 0 && (
                                    <Row label="Same file also">
                                        {detail.same_file_elsewhere.map((other) => `${other.user_name} (${other.name})`).join(', ')}
                                    </Row>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Heading>Where it came from</Heading>
                        {detail.source_message ? (
                            <>
                                <Row label="Message">
                                    {detail.source_message.role} via {detail.source_message.channel ?? '?'} ·{' '}
                                    {new Date(detail.source_message.timestamp).toLocaleString()} ·{' '}
                                    <RouterLink to={`/messages/${detail.user_id}`} onClick={onClose}>
                                        open this user’s messages
                                    </RouterLink>
                                </Row>
                                <Row label="Text">
                                    {detail.source_message.content ? (
                                        <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{detail.source_message.content}</Box>
                                    ) : (
                                        <Typography component="span" variant="body2" color="text.secondary">
                                            No text: the message was only files.
                                        </Typography>
                                    )}
                                </Row>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Not attached to a message: uploaded on its own, or its message was deleted.
                            </Typography>
                        )}

                        <Accordion disableGutters variant="outlined" defaultExpanded sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Extracted</AccordionSummary>
                            <AccordionDetails>
                                {detail.knowledge ? (
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 1.5,
                                            borderRadius: 1,
                                            backgroundColor: 'action.hover',
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {detail.knowledge.content}
                                        {(detail.knowledge.notes ?? []).length > 0 && (
                                            <Box sx={{ mt: 1.5 }}>
                                                <Typography variant="caption" color="text.secondary" component="div">
                                                    What the user said about it (outranks the above)
                                                </Typography>
                                                <Box component="ul" aria-label="User notes" sx={{ m: 0, pl: 2.5 }}>
                                                    {(detail.knowledge.notes ?? []).map((note, index) => (
                                                        <li key={index}>{note}</li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        {detail.knowledge.tags.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                {detail.knowledge.tags.map((tag) => (
                                                    <Chip key={tag} size="small" variant="outlined" label={tag} />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                                        {EXTRACTION_STATUS_NOTE[detail.status] ?? 'Nothing extracted yet.'}
                                    </Typography>
                                )}
                                {image && <FacesPanel faces={detail.faces ?? []} ready={detail.status === 'ready'} />}
                                <ExifPanel metadata={detail.metadata} isImage={image} />
                            </AccordionDetails>
                        </Accordion>
                    </>
                )}

                {detail && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <LoadingButton startIcon={<DownloadIcon />} onClick={download} loading={action === 'download'} disabled={busy || !detail.storage.file_present}>
                                Download
                            </LoadingButton>
                            {image && detail.status === 'failed' && (
                                <LoadingButton startIcon={<ReplayIcon />} onClick={retry} loading={action === 'retry'} disabled={busy || !detail.storage.file_present}>
                                    Retry reading
                                </LoadingButton>
                            )}
                            {image && (
                                <LoadingButton startIcon={<RefreshIcon />} onClick={readAgain} loading={action === 'read-again'} disabled={busy || !detail.storage.file_present}>
                                    Read metadata again
                                </LoadingButton>
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmingDelete(true)} disabled={busy}>
                                Delete
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
            <ConfirmDialog
                open={confirmingDelete}
                title="Delete this upload?"
                confirmLabel="Delete"
                onConfirm={remove}
                loading={action === 'delete'}
                onClose={() => setConfirmingDelete(false)}
            >
                This removes the row and the file from disk. {detail?.user_name ?? 'The owner'} can upload the same file again
                afterwards. It cannot be undone.
            </ConfirmDialog>
        </Dialog>
    );
};
